import './App.css';
import './assets/css/globals.css';
import Navbar from './components/NavBar';
import { Suspense } from 'react';
import { ChakraProvider, CSSReset, Box, Center, Spinner } from '@chakra-ui/react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { getStore } from './store';
import { AuthProvider, AuthContext } from './components/user/AuthProvider';
import lazyWithRetry from './utils/lazyWithRetry';
import customTheme from './components/customTheme';
import EnumPreloader from './components/EnumPreloader';
import Footer from './components/Footer';
import PrivateRoute from './PrivateRoute';
import GroupRoute from './GroupRoute';
import RoleRoute from './RoleRoute';
import ErrorPage from './pages/ErrorPage';
import ToastContainer from './utils/toastContainer';
import ContactUsDrawer from './components/forms/ContactUsDrawer';
import AskForLogin from './components/elastic/AskForLogin';
import BuyStart from './pages/homeServices/BuyStart';

// Route-level code splitting: each page loads as its own chunk on first visit.
const ElasticSearchComponent = lazyWithRetry(() => import('./components/elastic/SearchComponent'));
const UserProfileEdit = lazyWithRetry(() => import('./components/user/profileEdit'));
const NotificationSettingsPage = lazyWithRetry(() => import('./pages/NotificationSettingsPage'));
const MyReviewsPage = lazyWithRetry(() => import('./pages/MyReviewsPage'));
const WriteReviewPage = lazyWithRetry(() => import('./pages/WriteReviewPage'));
const VendorProfilePage = lazyWithRetry(() => import('./pages/VendorProfilePage'));
const Joingroup = lazyWithRetry(() => import('./components/user/Joingroup'));
const JoinTeam = lazyWithRetry(() => import('./components/groups/JoinTeam'));
const TermsPage = lazyWithRetry(() => import('./pages/static/TermsPage'));
const PrivacyPolicyPage = lazyWithRetry(() => import('./pages/static/PrivacyPolicyPage'));
const CookieSettingsPage = lazyWithRetry(() => import('./pages/static/CookieSettingsPage'));
const Dashboard = lazyWithRetry(() => import('./components/user/Dashboard'));
const AboutUsPage = lazyWithRetry(() => import('./pages/static/AboutUsPage'));
const DisclosuresPage = lazyWithRetry(() => import('./pages/static/DisclosuresPage'));
const UserManagement = lazyWithRetry(() => import('./components/admin/UserManagement'));
const UserUnSubscribe = lazyWithRetry(() => import('./pages/static/UserUnSubscribe'));
const TicketManagement = lazyWithRetry(() => import('./components/admin/TicketManagement'));
const CompanyManagement = lazyWithRetry(() => import('./components/admin/CompanyManagement'));
const SuccessPage = lazyWithRetry(() => import('./components/user/SuccessPage'));
const SignupSuccessPage = lazyWithRetry(() => import('./components/user/SignupSuccessPage'));
const PhoneVerifyByOtp = lazyWithRetry(() => import('./components/user/Phone-verify-by-otp'));
const CustomFieldsTable = lazyWithRetry(() => import('./components/admin/CustomFieldsTable'));
const SellStart = lazyWithRetry(() => import('./pages/homeServices/SellStart'));
const RequestProcess = lazyWithRetry(() => import('./pages/DetailsPage/RequestProcess'));
const RequestInvites = lazyWithRetry(() => import('./components/grid/Requestinvites'));
const RequestsGrid = lazyWithRetry(() => import('./components/admin/RequestsGrid'));
const ContactsList = lazyWithRetry(() => import('./components/admin/ContactsList'));
const Services = lazyWithRetry(() => import('./components/admin/Services'));
// const Signup = lazyWithRetry(() => import('./components/user/signup'));
// const AgentSignup = lazyWithRetry(() => import('./components/user/agent_signup'));
const SocialOnboarding = lazyWithRetry(() => import('./components/user/SocialOnboarding'));
const SignupStepAccount = lazyWithRetry(() => import('./components/user/signup-steps/SignupStepAccount'));
const SignupStepRole = lazyWithRetry(() => import('./components/user/signup-steps/SignupStepRole'));
const SignupStepServices = lazyWithRetry(() => import('./components/user/signup-steps/SignupStepServices'));
const SignupStepPhone = lazyWithRetry(() => import('./components/user/signup-steps/SignupStepPhone'));
const SignupStepProfile = lazyWithRetry(() => import('./components/user/signup-steps/SignupStepProfile'));
const ReferralFeeStructure = lazyWithRetry(() => import('./pages/static/ReferralFeeStructure'));
const CustomersPage = lazyWithRetry(() => import('./pages/static/CustomersPage'));
const RealtorPros = lazyWithRetry(() => import('./pages/static/RealtorPros'));
const RealtorAgents = lazyWithRetry(() => import('./pages/static/RealtorAgents'));
const EnumManager = lazyWithRetry(() => import('./components/admin/EnumManager'));
const TranslationManager = lazyWithRetry(() => import('./components/admin/TranslationManager'));
const TemplateManager = lazyWithRetry(() => import('./components/admin/TemplateManager'));
const SettingsAdminPage = lazyWithRetry(() => import('./modules/settings/SettingsAdminPage'));
const ContractsAdminPage = lazyWithRetry(() => import('./modules/contracts/ContractsAdminPage'));
const UserRule = lazyWithRetry(() => import('./modules/userRules/UserRulePage'));
const EmailAnalyticsPage = lazyWithRetry(() => import('./modules/emailAnalytics/EmailAnalyticsPage'));
const ReferralAgreementPage = lazyWithRetry(() => import('./pages/static/ReferralAgreementPage'));
const TcTermsPage = lazyWithRetry(() => import('./pages/static/tc/TcTermsPage'));
const TcPrivacyPage = lazyWithRetry(() => import('./pages/static/tc/TcPrivacyPage'));
const TcReferralAgreementPage = lazyWithRetry(() => import('./pages/static/tc/TcReferralAgreementPage'));
const MessagesPage = lazyWithRetry(() => import('./pages/MessagesPage'));

const store = getStore();

function App() {
  return (
    <Provider store={store}>
      <AuthProvider>
        <ChakraProvider theme={customTheme}>
          <CSSReset />
          <EnumPreloader>
          <Box minHeight="100vh">
              <Navbar />
              <Suspense fallback={<RouteFallback />}>
              <Routes>
                  <Route path="/" element={<BuyStart />} />
                  <Route path="/buy/start/" element={<BuyStart />} />
                  <Route path="/sell/start/" element={<SellStart />} />

                  {/* <Route path="/sign-up" element={<Signup />} />
                  <Route path="/pro-sign-up" element={<AgentSignup />} />
                  
                  // step-wizard signups (new design) — run alongside the single-page forms above.
                      customer: account → phone+OTP → names; pro: account → role → services → phone+OTP → names */}
                  <Route path="/signup" element={<SignupStepAccount flow="customer" />} />
                  <Route path="/signup/phone" element={<SignupStepPhone />} />
                  <Route path="/signup/profile" element={<SignupStepProfile />} />
                  <Route path="/pro-signup" element={<SignupStepAccount flow="pro" />} />
                  <Route path="/pro-signup/role" element={<SignupStepRole />} />
                  <Route path="/pro-signup/services" element={<SignupStepServices />} />
                  <Route path="/pro-signup/phone" element={<SignupStepPhone />} />
                  <Route path="/pro-signup/profile" element={<SignupStepProfile />} />
                  {/* transaction coordinator signup — not linked anywhere in the UI, the link is shared manually */}
                  <Route path="/tc-signup" element={<SignupStepAccount flow="tc" />} />
                  <Route path="/tc-signup/profile" element={<SignupStepProfile />} />
                  <Route path="/tc-signup/phone" element={<SignupStepPhone />} />
                  <Route path="/tc/terms/" element={<TcTermsPage />} />
                  <Route path="/tc/privacy/" element={<TcPrivacyPage />} />
                  <Route path="/tc/referral-agreement/" element={<TcReferralAgreementPage />} />
                  <Route path="/pro-referral-fee-structure" element={<ReferralFeeStructure />} />
                  <Route path="/welcome" element={<PrivateRoute><SocialOnboarding /></PrivateRoute>} />
                  <Route path="/login" element={<ProtectedContent />} />
                  <Route path="/agent/referral-agreement/" element={<ReferralAgreementPage />} />

                  <Route path="/search/" element={<PrivateRoute><ElasticSearchComponent /></PrivateRoute>} />
                  <Route path="/profile/:tab?" element={<PrivateRoute><UserProfileEdit /></PrivateRoute>} />
                  <Route path="/notification-settings" element={<PrivateRoute><NotificationSettingsPage /></PrivateRoute>} />
                  <Route path="/my-reviews" element={<PrivateRoute><MyReviewsPage /></PrivateRoute>} />
                  <Route path="/write-review" element={<PrivateRoute><WriteReviewPage /></PrivateRoute>} />
                  <Route path="/pro/:vendorId" element={<PrivateRoute><VendorProfilePage /></PrivateRoute>} />

                  <Route path="/customer/home/" element={<CustomersPage />} />
                  <Route path="/realtor-pros/home/" element={<RealtorPros />} />
                  <Route path="/realtor-agents/home/" element={<RealtorAgents />} />

                  <Route path="/terms/" element={<TermsPage />} />
                  <Route path="/privacy/" element={<PrivacyPolicyPage />} />
                  <Route path="/about-us/" element={<AboutUsPage />} />
                  <Route path="/disclosures/" element={<DisclosuresPage />} />
                 
                  <Route path="/cookie-policy/" element={<CookieSettingsPage />} />

                  {/* get started forms pages */}
                  <Route path="/success/" element={<SuccessPage />} />
                  <Route path="/signup-success/" element={<SignupSuccessPage />} />
                  <Route path="/verify-by-otp/" element={<PhoneVerifyByOtp />} />

                  <Route path="/notifications/:token?" element={<UserUnSubscribe />} />

                  <Route path="/dashboard/*" element={<PrivateRoute><Dashboard /></PrivateRoute>} />
                    
                  {/* Buyer Routes  */}
                  <Route path="/buyer/profile/:tab?" 
                    element={<PrivateRoute><UserProfileEdit /></PrivateRoute>} />
                  
                  <Route path="/staff/request-invites/" element={<GroupRoute allowedGroups={['admin','staff']}><RequestInvites /></GroupRoute>} />

                  {/* transaction_coordinator is a role, not a group (the user.role_groups enum has no group for it) */}
                  <Route path="/staff/buyer-requests/" element={<RoleRoute allowedGroups={['admin','staff']} allowedRoles={['transaction_coordinator']}><RequestsGrid /></RoleRoute>} />

                    {/* Admin Routes  */}
                  <Route path="/admin/profile/:tab" element={<GroupRoute allowedGroups={['admin']}><UserProfileEdit /></GroupRoute>} />
                  <Route path="/admin/users/:id?" element={<GroupRoute allowedGroups={['admin', 'staff']}><UserManagement /></GroupRoute>} />
                  <Route path="/admin/support-tickets/" element={<GroupRoute allowedGroups={['admin','staff']}><TicketManagement /></GroupRoute>} />
          
                  {/* Staff Routes  */}
                  <Route path="/staff/profile/:tab" element={<GroupRoute allowedGroups={['admin','staff']}><UserProfileEdit /></GroupRoute>} />
                  <Route path="/staff/users/:id?" element={<GroupRoute allowedGroups={['admin','staff']}><UserManagement /></GroupRoute>} />
                  <Route path="/staff/support-tickets/" element={<GroupRoute allowedGroups={['admin','staff']}><TicketManagement /></GroupRoute>} />
                  <Route path="/staff/contacts/" 
                    element={<RoleRoute allowedGroups={['admin','staff']} allowedRoles={['transaction_coordinator']}>
                        <ContactsList /></RoleRoute>} />
                  
                  
                  <Route path="/staff/companies/" 
                    element={<GroupRoute allowedGroups={['admin','staff']}><CompanyManagement /></GroupRoute>} />

                  {/* Admin Routes — admin can access all; other roles are
                      restricted to their permitted components via RoleRoute */}
                  <Route path="/admin/request-invites/" element={<PrivateRoute><RequestInvites /></PrivateRoute>} />
                  <Route path="/admin/profile/:tab" element={<PrivateRoute><UserProfileEdit /></PrivateRoute>} />

                  <Route path="/admin/customer-requests/" element={<RoleRoute allowedRoles={['admin','staff', 'transaction_coordinator']}><RequestsGrid /></RoleRoute>} />
                  <Route path="/admin/users/:id?" element={<RoleRoute allowedRoles={['admin','staff']}><UserManagement /></RoleRoute>} />
                  <Route path="/admin/support-tickets/" element={<RoleRoute allowedRoles={['admin','staff', 'dataentry']}><TicketManagement /></RoleRoute>} />
                  <Route path="/admin/companies/" element={<RoleRoute allowedRoles={['admin','staff', 'dataentry']}><CompanyManagement /></RoleRoute>} />
                  <Route path="/admin/contacts/" element={<RoleRoute allowedRoles={['admin','staff','transaction_coordinator']}><ContactsList /></RoleRoute>} />
                  <Route path="/admin/custom-fields/" element={<RoleRoute allowedRoles={['admin','staff']}><CustomFieldsTable /></RoleRoute>} />
                  <Route path="/admin/services/" element={<RoleRoute allowedRoles={[]}><Services /></RoleRoute>} />

                  {/* Profile routes for other roles */}
                  <Route path="/agent/profile/:tab?" element={<PrivateRoute><UserProfileEdit /></PrivateRoute>} />
                  <Route path="/builder/profile/:tab" element={<PrivateRoute><UserProfileEdit /></PrivateRoute>} />

                  <Route path="/groups/joingroup" element={<PrivateRoute><Joingroup /></PrivateRoute>} />
                  {/* Team invitation landing page. Deliberately NOT PrivateRoute:
                      the invite-details endpoint is public, so an invitee can see
                      what they were invited to before signing in. The page itself
                      requires a session only to accept/decline. */}
                  <Route path="/teams/join" element={<JoinTeam />} />
                  <Route path="/messages/" element={<PrivateRoute><MessagesPage /></PrivateRoute>} />

                  <Route path="/request-process/:token" element={<PrivateRoute><RequestProcess /></PrivateRoute>} />
              
                  <Route path="/admin/custom-fields/" element={<GroupRoute allowedGroups={['admin']}><CustomFieldsTable /></GroupRoute>} />
                  <Route path="/admin/services/" element={<GroupRoute allowedGroups={['admin']}><Services /></GroupRoute>} />
                  <Route path="/admin/enums/" element={<GroupRoute allowedGroups={['admin']}><EnumManager /></GroupRoute>} />
                  <Route path="/admin/translations/" element={<GroupRoute allowedGroups={['admin','staff']}><TranslationManager /></GroupRoute>} />
                  <Route path="/admin/templates/" element={<GroupRoute allowedGroups={['admin','staff']}><TemplateManager /></GroupRoute>} />
                  <Route path="/admin/settings-admin/" element={<GroupRoute allowedGroups={['admin']}><SettingsAdminPage readOnly={false} /></GroupRoute>} />
                  <Route path="/admin/user-rules/" element={<GroupRoute allowedGroups={['admin']}><UserRule readOnly={false} /></GroupRoute>} />
                  <Route path="/admin/email-analytics/" element={<GroupRoute allowedGroups={['admin']}><EmailAnalyticsPage /></GroupRoute>} />
                  <Route path="/staff/settings-admin/" element={<GroupRoute allowedGroups={['admin','staff']}><SettingsAdminPage readOnly={true} /></GroupRoute>} />
                  <Route path="/admin/contracts-admin/" element={<GroupRoute allowedGroups={['admin']}><ContractsAdminPage readOnly={false} /></GroupRoute>} />
                  <Route path="/staff/contracts-admin/" element={<GroupRoute allowedGroups={['admin','staff']}><ContractsAdminPage readOnly={true} /></GroupRoute>} />
                  <Route path="/unauthorized" element={<div style={{padding:'2rem',textAlign:'center'}}><h2>403 — Access Denied</h2><p>You do not have permission to view this page.</p></div>} />

                  {/* SEO-friendly service pages: /<service-slug>/<id> and /sell/<service-slug>/<id>.
                      Static routes above always rank higher than these, so they
                      don't shadow pages like /signup/phone or /pro/:vendorId. */}
                  <Route path="/sell/:serviceSlug/:serviceId" element={<SellStart />} />
                  <Route path="/:serviceSlug/:serviceId" element={<BuyStart />} />
                  <Route path="*" element={<ErrorPage />} />


                </Routes>
              </Suspense>
              <FooterControlled />
            <ToastContainer />
            <ContactUsDrawer />
            <AskForLogin />
          </Box>
          </EnumPreloader>
        </ChakraProvider>
      </AuthProvider>
    </Provider>
  );
}

const RouteFallback = () => (
  <Center minHeight="60vh">
    <Spinner size="lg" />
  </Center>
);

const FooterControlled = () => {

  return <Footer />;
};

const ProtectedContent = () => {

  return (
    <AuthContext.Consumer>
      {({ isAuthenticated, keycloak }) =>
        isAuthenticated ? <Navigate to="/buy/start/" /> : (
          <div className="min-h-svh" style={{textAlign:"center", padding:"5px"}}>loading...</div>
        )
      }
    </AuthContext.Consumer>
  );
};

export default App;
